import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  Badge,
  Tooltip
} from '@mui/material';
import {
  School,
  TrendingUp,
  EmojiEvents,
  Insights,
  ExpandMore,
  AutoAwesome,
  Speed,
  TrackChanges
} from '@mui/icons-material';
import { InterviewSummary } from '../types';

interface LearningSession {
  id: string;
  date: Date;
  questionsAsked: number;
  questionsAnswered: number;
  newKnowledgeGained: string[];
  performanceScore: number;
  duration: number; // in minutes
  topics: string[];
}

interface LearningBadge {
  id: string;
  name: string;
  description: string;
  icon: string;
  earned: boolean;
  earnedDate?: Date;
  progress: number;
  requirement: string;
}

interface ProgressiveLearningProps {
  currentKnowledge?: InterviewSummary | null;
  recentQueries: string[];
  onLevelUp?: (newLevel: number) => void;
}

export function ProgressiveLearning({ 
  currentKnowledge, 
  recentQueries, 
  onLevelUp 
}: ProgressiveLearningProps) {
  const [learningLevel, setLearningLevel] = useState(1);
  const [experiencePoints, setExperiencePoints] = useState(0);
  const [learningSessions, setLearningSessions] = useState<LearningSession[]>([]);
  const [badges, setBadges] = useState<LearningBadge[]>([]);
  const [weeklyGoal, setWeeklyGoal] = useState(10);
  const [currentStreak, setCurrentStreak] = useState(0);

  // Initialize badges and learning data
  useEffect(() => {
    initializeBadges();
    loadLearningProgress();
  }, []);

  const checkLevelUp = React.useCallback((xp: number) => {
    const newLevel = Math.floor(xp / 100) + 1;
    if (newLevel > learningLevel) {
      setLearningLevel(newLevel);
      onLevelUp?.(newLevel);
    }
  }, [learningLevel, onLevelUp]);

  // Update experience based on current knowledge
  useEffect(() => {
    if (currentKnowledge) {
      const knowledgeXP = calculateKnowledgeXP(currentKnowledge);
      setExperiencePoints(prev => {
        const newXP = Math.max(prev, knowledgeXP);
        checkLevelUp(newXP);
        return newXP;
      });
    }
  }, [currentKnowledge, checkLevelUp]);

  const initializeBadges = () => {
    const initialBadges: LearningBadge[] = [
      {
        id: 'first_interview',
        name: 'First Contact',
        description: 'Complete your first knowledge interview',
        icon: '🎯',
        earned: false,
        progress: 0,
        requirement: 'Complete 1 interview'
      },
      {
        id: 'table_explorer',
        name: 'Table Explorer',
        description: 'Discover 5 database tables',
        icon: '🗺️',
        earned: false,
        progress: 0,
        requirement: 'Discover 5 tables'
      },
      {
        id: 'relationship_master',
        name: 'Relationship Master',
        description: 'Learn 10 table relationships',
        icon: '🔗',
        earned: false,
        progress: 0,
        requirement: 'Learn 10 relationships'
      },
      {
        id: 'business_guru',
        name: 'Business Guru',
        description: 'Understand 15 business rules',
        icon: '💼',
        earned: false,
        progress: 0,
        requirement: 'Learn 15 business rules'
      },
      {
        id: 'query_champion',
        name: 'Query Champion',
        description: 'Ask 50 successful questions',
        icon: '🏆',
        earned: false,
        progress: 0,
        requirement: 'Ask 50 questions'
      },
      {
        id: 'streak_master',
        name: 'Streak Master',
        description: 'Maintain a 7-day learning streak',
        icon: '🔥',
        earned: false,
        progress: 0,
        requirement: '7-day streak'
      },
      {
        id: 'knowledge_sage',
        name: 'Knowledge Sage',
        description: 'Reach 95% query success rate',
        icon: '🧙‍♂️',
        earned: false,
        progress: 0,
        requirement: '95% success rate'
      }
    ];
    setBadges(initialBadges);
  };

  const loadLearningProgress = () => {
    // Simulate loading saved progress
    const savedProgress = localStorage.getItem('sleekshop_learning_progress');
    if (savedProgress) {
      const progress = JSON.parse(savedProgress);
      setLearningLevel(progress.level || 1);
      setExperiencePoints(progress.xp || 0);
      setCurrentStreak(progress.streak || 0);
      setLearningSessions(progress.sessions || []);
    }
  };

  const saveLearningProgress = useCallback(() => {
    const progressData = {
      level: learningLevel,
      xp: experiencePoints,
      streak: currentStreak,
      sessions: learningSessions,
      lastUpdated: new Date().toISOString()
    };
    localStorage.setItem('sleekshop_learning_progress', JSON.stringify(progressData));
  }, [learningLevel, experiencePoints, currentStreak, learningSessions]);

  const calculateKnowledgeXP = (knowledge: InterviewSummary): number => {
    let xp = 0;
    xp += knowledge.tablesDiscovered.length * 10; // 10 XP per table
    xp += knowledge.relationshipsLearned.length * 15; // 15 XP per relationship
    xp += knowledge.businessRules.length * 20; // 20 XP per business rule
    xp += knowledge.totalInsights * 5; // 5 XP per insight
    xp += Math.round(knowledge.confidenceScore * 100); // Confidence bonus
    return xp;
  };



  const updateBadgeProgress = useCallback(() => {
    if (!currentKnowledge) return;

    setBadges(prevBadges => 
      prevBadges.map(badge => {
        let progress = badge.progress;
        let earned = badge.earned;

        switch (badge.id) {
          case 'first_interview':
            progress = currentKnowledge ? 100 : 0;
            earned = currentKnowledge !== null;
            break;
          case 'table_explorer':
            progress = Math.min(100, (currentKnowledge.tablesDiscovered.length / 5) * 100);
            earned = currentKnowledge.tablesDiscovered.length >= 5;
            break;
          case 'relationship_master':
            progress = Math.min(100, (currentKnowledge.relationshipsLearned.length / 10) * 100);
            earned = currentKnowledge.relationshipsLearned.length >= 10;
            break;
          case 'business_guru':
            progress = Math.min(100, (currentKnowledge.businessRules.length / 15) * 100);
            earned = currentKnowledge.businessRules.length >= 15;
            break;
          case 'query_champion':
            progress = Math.min(100, (recentQueries.length / 50) * 100);
            earned = recentQueries.length >= 50;
            break;
          case 'knowledge_sage':
            progress = Math.min(100, currentKnowledge.confidenceScore * 100);
            earned = currentKnowledge.confidenceScore >= 0.95;
            break;
        }

        return { ...badge, progress, earned, earnedDate: earned && !badge.earned ? new Date() : badge.earnedDate };
      })
    );
  }, [currentKnowledge, recentQueries]);

  useEffect(() => {
    updateBadgeProgress();
  }, [updateBadgeProgress]);

  useEffect(() => {
    saveLearningProgress();
  }, [saveLearningProgress]);

  const getXPToNextLevel = () => {
    const nextLevelXP = learningLevel * 100;
    return nextLevelXP - experiencePoints;
  };

  const getLevelProgress = () => {
    const xpInCurrentLevel = experiencePoints - ((learningLevel - 1) * 100);
    return (xpInCurrentLevel / 100) * 100;
  };

  const earnedBadges = badges.filter(badge => badge.earned);
  const availableBadges = badges.filter(badge => !badge.earned);

  return (
    <Box>
      {/* Learning Level & Progress */}
      <Card sx={{ mb: 2, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', mr: 2 }}>
                <School />
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  Level {learningLevel} Knowledge Expert
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {experiencePoints} XP • {getXPToNextLevel()} XP to next level
                </Typography>
              </Box>
            </Box>
            <Badge badgeContent={currentStreak} color="secondary">
              <Tooltip title={`${currentStreak} day learning streak`}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h3">🔥</Typography>
                  <Typography variant="caption">Streak</Typography>
                </Box>
              </Tooltip>
            </Badge>
          </Box>
          
          <LinearProgress 
            variant="determinate" 
            value={getLevelProgress()} 
            sx={{ 
              height: 8, 
              borderRadius: 4,
              backgroundColor: 'rgba(255,255,255,0.2)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#fff'
              }
            }}
          />
        </CardContent>
      </Card>

      {/* Badges Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <EmojiEvents sx={{ mr: 1, color: 'primary.main' }} />
            Achievements ({earnedBadges.length}/{badges.length})
          </Typography>
          
          {earnedBadges.length > 0 && (
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle1">Earned Badges ({earnedBadges.length})</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {earnedBadges.map((badge) => (
                    <Tooltip key={badge.id} title={`${badge.description} - Earned ${badge.earnedDate?.toLocaleDateString()}`}>
                      <Box sx={{ 
                        textAlign: 'center', 
                        p: 2, 
                        backgroundColor: 'success.light', 
                        borderRadius: 2,
                        minWidth: 120
                      }}>
                        <Typography variant="h4">{badge.icon}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {badge.name}
                        </Typography>
                        <Chip label="Earned" color="success" size="small" />
                      </Box>
                    </Tooltip>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          )}

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle1">Available Badges ({availableBadges.length})</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {availableBadges.map((badge) => (
                  <Tooltip key={badge.id} title={badge.description}>
                    <Box sx={{ 
                      textAlign: 'center', 
                      p: 2, 
                      backgroundColor: 'background.default', 
                      borderRadius: 2,
                      minWidth: 120,
                      opacity: 0.7
                    }}>
                      <Typography variant="h4">{badge.icon}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {badge.name}
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={badge.progress} 
                        sx={{ mt: 1, height: 4, borderRadius: 2 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {Math.round(badge.progress)}%
                      </Typography>
                    </Box>
                  </Tooltip>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>

      {/* Weekly Goals */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <TrackChanges sx={{ mr: 1, color: 'primary.main' }} />
            Weekly Learning Goal
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" gutterBottom>
                Questions asked this week: {recentQueries.length} / {weeklyGoal}
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={Math.min(100, (recentQueries.length / weeklyGoal) * 100)}
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Box>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setWeeklyGoal(prev => prev + 5)}
            >
              Increase Goal
            </Button>
          </Box>
          
          {recentQueries.length >= weeklyGoal && (
            <Alert severity="success">
              🎉 Congratulations! You've reached your weekly learning goal!
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Learning Insights */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <Insights sx={{ mr: 1, color: 'primary.main' }} />
            Learning Insights
          </Typography>
          
          <List dense>
            <ListItem>
              <ListItemIcon>
                <TrendingUp color="success" />
              </ListItemIcon>
              <ListItemText
                primary="Knowledge Growth"
                secondary={`You've learned about ${currentKnowledge?.tablesDiscovered.length || 0} tables and ${currentKnowledge?.relationshipsLearned.length || 0} relationships`}
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <Speed color="info" />
              </ListItemIcon>
              <ListItemText
                primary="Learning Velocity"
                secondary={`Current XP: ${experiencePoints} | Level ${learningLevel} Knowledge Expert`}
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <AutoAwesome color="warning" />
              </ListItemIcon>
              <ListItemText
                primary="Next Milestone"
                secondary={
                  availableBadges.length > 0 
                    ? `Work towards "${availableBadges[0].name}" - ${availableBadges[0].requirement}`
                    : "All badges earned! You're a knowledge master!"
                }
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>
    </Box>
  );
}
